/**
 * Resume Zustand store following ADR-021 sliced store with immer middleware.
 *
 * State:  { resumes, selectedId, isLoading, error }
 * Actions: { fetchResumes, createResume, updateResume, deleteResume, selectResume }
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Resume } from '../types';
import { apiClient } from '../services/api.client';
import { logger } from '../services/logger.client';

const log = logger.child({ module: 'resumeStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface ResumeState {
  resumes: Resume[];
  selectedId: string | null;
  isLoading: boolean;
  error: string | null;
  /** Set to true after the first fetch completes (success or error). */
  hasFetched: boolean;
}

interface ResumeActions {
  fetchResumes: () => Promise<void>;
  createResume: (title: string) => Promise<Resume | null>;
  updateResume: (id: string, patch: Partial<Resume>) => Promise<void>;
  deleteResume: (id: string) => Promise<void>;
  selectResume: (id: string | null) => void;
  clearError: () => void;
}

export type ResumeStore = ResumeState & { actions: ResumeActions };

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useResumeStore = create<ResumeStore>()(
  immer((set) => ({
    /* ---------- initial state ---------- */
    resumes: [],
    selectedId: null,
    isLoading: false,
    error: null,
    hasFetched: false,

    /* ---------- actions ---------- */
    actions: {
      fetchResumes: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Fetching resumes');
          const resumes = await apiClient<Resume[]>('/api/v1/resumes');

          set((state) => {
            state.resumes = resumes;
            state.isLoading = false;
            state.hasFetched = true;
          });

          log.info('Resumes fetched', { count: resumes.length });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to fetch resumes';
          log.error('Failed to fetch resumes', { error: message });

          set((state) => {
            state.isLoading = false;
            state.hasFetched = true;
            state.error = message;
          });
        }
      },

      createResume: async (title: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Creating resume', { title });

          const resume = await apiClient<Resume>('/api/v1/resumes', {
            method: 'POST',
            body: JSON.stringify({
              title,
              summary: '',
              status: 'draft',
              skills: [],
              workExperience: [],
            }),
          });

          set((state) => {
            state.resumes.push(resume);
            state.selectedId = resume.id;
            state.isLoading = false;
          });

          log.info('Resume created', { id: resume.id });
          return resume;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to create resume';
          log.error('Failed to create resume', { error: message });

          set((state) => {
            state.isLoading = false;
            state.error = message;
          });
          return null;
        }
      },

      updateResume: async (id: string, patch: Partial<Resume>) => {
        set((state) => {
          state.error = null;
        });

        try {
          log.info('Updating resume', { id });

          const updated = await apiClient<Resume>(`/api/v1/resumes/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          });

          set((state) => {
            const idx = state.resumes.findIndex((r) => r.id === id);
            if (idx !== -1) {
              state.resumes[idx] = updated;
            }
          });

          log.info('Resume updated', { id });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to update resume';
          log.error('Failed to update resume', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      deleteResume: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          log.info('Deleting resume', { id });

          await apiClient<void>(`/api/v1/resumes/${id}`, {
            method: 'DELETE',
          });

          set((state) => {
            state.resumes = state.resumes.filter((r) => r.id !== id);
            if (state.selectedId === id) {
              state.selectedId = null;
            }
          });

          log.info('Resume deleted', { id });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to delete resume';
          log.error('Failed to delete resume', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      selectResume: (id: string | null) => {
        set((state) => {
          state.selectedId = id;
        });
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    },
  })),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks (ADR-021 wrapper-hooks pattern)                    */
/* ------------------------------------------------------------------ */

/** Stable reference to actions -- never causes a re-render. */
export function useResumeActions() {
  return useResumeStore((s) => s.actions);
}

/** Get the currently selected resume. */
export function useSelectedResume(): Resume | null {
  const resumes = useResumeStore((s) => s.resumes);
  const selectedId = useResumeStore((s) => s.selectedId);

  return resumes.find((r) => r.id === selectedId) ?? null;
}
